import {
  Alert,
  Checkbox,
  FormInput,
  FormInputErrorMessage,
  Label,
} from '@clearcover/atlas';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getFilteredUsageDetailsOptions,
  isUncheckedOptionsValues,
  isUncheckedUsageDetailsOptions,
  VehicleFormFieldKeys,
} from '../options';
import { useAppContext } from 'QuoteApp/services/providers';
import { useFormikContext } from 'formik';
import {
  FormValuesType,
  VehicleFieldsType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { Column, Row } from 'Shared/components';

import styles from './styles.module.css';
import { stringToBoolean } from 'Shared/utils';
import { generateFieldDataTestId } from 'QuoteApp/utils';

export const VehicleUsage = ({
  readOnly,
  vehicleId,
  getInputProps,
  getFullFieldName,
}: {
  readOnly: boolean;
  vehicleId: string;
  getInputProps: (fieldName: VehicleFormFieldKeys, required?: any) => any;
  getFullFieldName: (
    fieldName: keyof Omit<VehicleFieldsType, 'editable' | 'lienHolder'>
  ) => string;
}) => {
  const { config, details } = useAppContext();
  const { values, isSubmitting, setFieldValue } = useFormikContext<
    FormValuesType
  >();

  const { vehicles } = config || {};

  const filterUsageDetailsOptions = useMemo(() => {
    if (vehicles) {
      return getFilteredUsageDetailsOptions(vehicles.usageDetails);
    } else {
      return [];
    }
  }, [vehicles]);

  const allFieldsUnchecked = useMemo(() => {
    if (vehicles) {
      return isUncheckedUsageDetailsOptions(
        vehicles.usageDetails,
        details.vehicles[vehicleId]
      );
    } else {
      return false;
    }
  }, [vehicles, details.vehicles, vehicleId]);

  const allFieldsEmpty = useMemo(
    () =>
      filterUsageDetailsOptions.every(
        questionKey => values.vehicles[vehicleId][questionKey.key] === ''
      ),
    [filterUsageDetailsOptions, values.vehicles, vehicleId]
  );

  const [noneOfAbove, setNoneOfAbove] = useState<boolean>(false);
  const [doesUsagesApply, setDoesUsagesApply] = useState<boolean | undefined>(
    allFieldsEmpty ? undefined : !allFieldsUnchecked
  );

  const noOptionsApplyDisabled = filterUsageDetailsOptions.every(
    ({ key }) => getInputProps(key).disabled
  );

  const allValuesUnchecked = useMemo(() => {
    if (vehicles) {
      return isUncheckedOptionsValues(
        vehicles.usageDetails,
        values.vehicles[vehicleId]
      );
    } else {
      return false;
    }
  }, [values.vehicles, vehicleId, vehicles]);

  const setAllFielsToSameValue = useCallback(
    (value: boolean | string, force = false) => {
      for (const { key } of filterUsageDetailsOptions) {
        if (values.vehicles[vehicleId][key] === '' && !force) continue;
        setFieldValue(getFullFieldName(key), value);
      }
    },
    [
      filterUsageDetailsOptions,
      getFullFieldName,
      setFieldValue,
      values.vehicles,
      vehicleId,
    ]
  );

  useEffect(() => {
    if (!config?.vehicles.usageDetails.required) return;

    if (!noneOfAbove && doesUsagesApply) {
      const vehicleValues = values.vehicles[vehicleId];

      if (allValuesUnchecked) {
        setAllFielsToSameValue('');
      } else {
        for (const { key } of filterUsageDetailsOptions) {
          const val = vehicleValues[key] === 'true' ? 'true' : 'false';
          if (vehicleValues[key] === val) continue;
          setFieldValue(getFullFieldName(key), val);
        }
      }
    }
  }, [
    vehicleId,
    values.vehicles,
    filterUsageDetailsOptions,
    allValuesUnchecked,
    getFullFieldName,
    setFieldValue,
    config?.vehicles.usageDetails.required,
    noneOfAbove,
    doesUsagesApply,
    setAllFielsToSameValue,
  ]);

  const renderReadOnlyQuestion = () => (
    <>
      <Row>
        <ul className={styles.vehicleUsageList}>
          {filterUsageDetailsOptions.map(option => (
            <li key={option.key}>
              <Label {...({} as any)}>{option.label}</Label>
            </li>
          ))}
        </ul>
      </Row>
      <Row>
        <Column variant="third">
          <FormInput
            data-testid="does-vehicle-usage-apply"
            name={`vehicles-${vehicleId}-does-vehicle-usage-apply`}
            value={doesUsagesApply?.toString() || ''}
            variant={'radioButtonGroup'}
            onChange={(value: string) => {
              const booleanValue = stringToBoolean(value);
              setDoesUsagesApply(booleanValue);

              if (!booleanValue) setAllFielsToSameValue(false, true);
            }}
            options={{ true: 'Yes', false: 'No' }}
            disabled={readOnly}
          />
        </Column>
      </Row>
    </>
  );

  const renderQuestionWithCheckbox = () => (
    <>
      {filterUsageDetailsOptions.map(option => (
        <Row key={option.key}>
          <Checkbox
            data-testid={`usage-details-${generateFieldDataTestId(option.key)}`}
            className={styles.checkbox}
            {...getInputProps(option.key)}
            value={values.vehicles?.[vehicleId][option.key] === 'true'}
            label={option.label}
            onChange={value => {
              if (isSubmitting) return;
              setNoneOfAbove(false);
              setFieldValue(getFullFieldName(option.key), value.toString());
            }}
          />
        </Row>
      ))}
      <Row>
        <Checkbox
          disabled={noOptionsApplyDisabled}
          name="no-options-apply"
          label="None of these apply"
          data-testid="usage-details-no-options-apply"
          value={noneOfAbove}
          className={styles.checkbox}
          onChange={value => {
            if (isSubmitting) return;
            setNoneOfAbove(value);
            for (const { key } of filterUsageDetailsOptions) {
              setFieldValue(getFullFieldName(key), 'false');
            }
          }}
        />
      </Row>
      {!readOnly && (
        <Row>
          <Alert
            type="info"
            message="Take a moment to double-check your answers before submitting."
          />
        </Row>
      )}
    </>
  );

  const required = useMemo(() => {
    if (doesUsagesApply === undefined) return true;

    if (
      doesUsagesApply &&
      !noneOfAbove &&
      (allFieldsUnchecked || allValuesUnchecked)
    )
      return true;

    return false;
  }, [allFieldsUnchecked, allValuesUnchecked, noneOfAbove, doesUsagesApply]);

  return (
    <div aria-labelledby="usage-details" className={styles.vehicleUsageWrapper}>
      <div className={styles.labelWrapper}>
        <Label
          {...({
            id: 'usage-details',
            className: styles.vehicleUsageLabel,
          } as any)}
        >
          {doesUsagesApply
            ? 'Please confirm which of these scenarios apply to this vehicle.'
            : 'Do any of these scenarios apply to this vehicle?'}
        </Label>
        {required && (
          <FormInputErrorMessage
            message="Required"
            className={styles.usageDetailsError}
          />
        )}
      </div>
      {doesUsagesApply
        ? renderQuestionWithCheckbox()
        : renderReadOnlyQuestion()}
    </div>
  );
};
